import { makeObservable, observable, runInAction, computed, action } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';
import { MAX_TRAINING_ANSWERS } from '../../../../core/constants';
import { MatenRequestResponse } from '../../../../shared';

class TrainingQuestionAnswer {
    public answer_id: number | null = null;
    public answer_text: string = '';
    public isCorrect: boolean = false;

    constructor(a_id: number | null = null, a_text: string = '', is_correct: boolean = false) {
        makeObservable(this, {
            answer_id: observable,
            answer_text: observable,
            isCorrect: observable,
            text: computed,
            updateText: action,
            swtichCorrect: action,
            isValid: computed,
        });

        this.answer_id = a_id;
        this.answer_text = a_text;
        this.isCorrect = is_correct;
    }

    public updateText(text: string): void {
        if (text.length < 80) {
            this.answer_text = text;
        }
    }

    public swtichCorrect(): void {
        this.isCorrect = !this.isCorrect;
    }

    get id(): number | null {
        return this.answer_id || null;
    }

    get text(): string {
        return this.answer_text || '';
    }

    get isValid(): boolean {
        return this.answer_text.length >= 3 && this.answer_text.length < 250;
    }
}
class TrainingQuestion {
    public question_id: number | null = null;
    public question_text: string = '';

    constructor(q_text: string, q_id: number | null = null) {
        makeObservable(this, {
            question_id: observable,
            question_text: observable,
            updateText: action,
            text: computed,
            id: computed,
            isValid: computed,
        });

        this.question_id = q_id;
        this.question_text = q_text;
    }

    public updateText(text: string): void {
        if (text.length < 80) {
            this.question_text = text;
        }
    }

    get text(): string {
        return this.question_text || '';
    }

    get id(): number | null {
        return this.question_id;
    }

    get isValid(): boolean {
        return this.question_text.length >= 3 && this.question_text.length < 80;
    }
}

class TrainingQuestionEditor {
    public question: TrainingQuestion | null = null;
    public answers: Array<TrainingQuestionAnswer> = [];

    constructor(question: any) {
        makeObservable(this, {
            question: observable,
            answers: observable,
            removeAnswer: action,
            addAnswer: action,
            maximumAnswersReached: computed,
            isValid: computed,
            isNewQuestion: computed,
            id: computed,
        });

        runInAction(() => {
            this.question = new TrainingQuestion(question?.text || '', question?.id || null);
            this.answers = [];

            if (question?.answers) {
                this.answers = question.answers.map(
                    (answer: any) =>
                        new TrainingQuestionAnswer(answer.id, answer.text, !!answer.isCorrect)
                );
            }
        });
    }

    public removeAnswer(answer_id: number): void {
        const index: number = (this.answers || []).findIndex(
            (a: TrainingQuestionAnswer) => a.answer_id === answer_id
        );
        if (index >= 0) {
            this.answers?.splice(index, 1);
        }
    }

    public addAnswer(): void {
        if (this.answers) {
            if (this.maximumAnswersReached) {
                return;
            }

            this.answers.push(new TrainingQuestionAnswer());
        }
    }

    get id(): number {
        return this.question?.id || 0;
    }

    get isNewQuestion(): boolean {
        return this.question?.id === null;
    }

    get maximumAnswersReached(): boolean {
        return this.answers ? this.answers.length >= MAX_TRAINING_ANSWERS : false;
    }

    get isValid(): boolean {
        return (
            !!this.question?.isValid &&
            this.answers?.length > 1 &&
            this.answers.every((answer: TrainingQuestionAnswer) => answer.isValid) &&
            this.answers.some((answer: TrainingQuestionAnswer) => answer.isCorrect)
        );
    }
}

/**
 * The dialog to edit or create trainings
 */
class TrainingEditDialogStore {
    private sdk: ApplicationSdk;
    public _cache: string = '';
    public training_loading: boolean = false;

    public activeTab: number = 0;
    public folder_id: number | null = null;

    public training_image: any = null;
    public training_image_uuid: string | null = null;

    public training_id: number | null = null;
    public training_name: string = '';
    public training_description: string = '';
    public training_video: string = '';
    public training_content_type: 'video' | 'pdf' = 'video';
    public training_content_file: string | null = null;
    public training_image_url: string | null = null;
    public training_active: boolean = false;

    public parent_folders: any[] = [];

    public training_questions: Array<any> = [];
    public menu_active_question: number | null = null;

    // Employee Results for Tab 2
    public employee_results: Array<any> = [];
    public employee_results_loading: boolean = false;
    public employee_results_pagination: any = null;
    public employee_results_sort_field: string = 'status_priority';
    public employee_results_sort_order: string = 'desc';
    public employee_results_current_page: number = 1;
    public employee_results_page_size: number = 10;

    public editor: TrainingQuestionEditor | null = null;
    public s3url: string = '';
    public s3urlPdf: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            activeTab: observable,
            _cache: observable,
            training_loading: observable,
            training_image: observable,
            parent_folders: observable,
            training_image_uuid: observable,
            folder_id: observable,
            training_id: observable,
            training_name: observable,
            training_description: observable,
            training_video: observable,
            training_content_type: observable,
            training_content_file: observable,
            training_image_url: observable,
            training_questions: observable,
            menu_active_question: observable,
            training_active: observable,
            employee_results: observable,
            employee_results_loading: observable,
            employee_results_pagination: observable,
            employee_results_sort_field: observable,
            employee_results_sort_order: observable,
            employee_results_current_page: observable,
            employee_results_page_size: observable,
            editor: observable,
            openQuestionEditor: action,
            changeOrderQuestion: action,
            toggleActive: action,
            setFolderId: action,
            setImage: action,
            isTrainingNameValid: computed,
            isTrainingVideoValid: computed,
            isValid: computed,
            trainingName: computed,
            trainingVideo: computed,
            trainingContentType: computed,
            trainingContentFile: computed,
            isTrainingContentValid: computed,
            trainingPdfUrl: computed,
            uploadPdf: action,
            openVideoUrl: action,
            openPdfUrl: action,
            trainingDescription: computed,
            allQuestions: computed,
            menuActiveQuestion: computed,
            setMenuActiveQuestion: action,
            closeQuestionEditor: action,
            allQuestionOrder: computed,
            cache: computed,
            hasChanged: computed,
            image: computed,
            isActive: computed,
            allParentFolders: computed,
            currentParentFolder: computed,
            isLoading: computed,
            onChangeTab: action,
            employeeResults: computed,
            isLoadingResults: computed,
            fetchEmployeeResults: action,
            onEmployeeResultsSort: action,
            onEmployeeResultsPageChange: action,
        });

        this.sdk = sdk;
    }

    get isLoading(): boolean {
        return this.training_loading;
    }

    get employeeResults(): Array<any> {
        return this.employee_results || [];
    }

    get isLoadingResults(): boolean {
        return this.employee_results_loading;
    }

    public async fetchEmployeeResults(): Promise<void> {
        if (!this.training_id) {
            return;
        }

        runInAction(() => {
            this.employee_results_loading = true;
        });

        try {
            const resultsResponse = await this.sdk.fetchApi('training', 'results', {
                training_id: this.training_id,
                page: this.employee_results_current_page,
                limit: this.employee_results_page_size,
                sortvalue: this.employee_results_sort_field,
                sort: this.employee_results_sort_order,
            });

            if (resultsResponse?.status === 200) {
                runInAction(() => {
                    const { data, pagination } = resultsResponse.json || {};
                    this.employee_results = data || [];
                    this.employee_results_pagination = pagination || null;
                });
            }
        } catch (error) {
            console.error('Error fetching employee results:', error);
            runInAction(() => {
                this.employee_results = [];
                this.employee_results_pagination = null;
            });
        } finally {
            runInAction(() => {
                this.employee_results_loading = false;
            });
        }
    }

    public async onEmployeeResultsSort(field: string, order: string): Promise<void> {
        runInAction(() => {
            this.employee_results_sort_field = field;
            this.employee_results_sort_order = order;
            this.employee_results_current_page = 1; // Reset to first page when sorting
        });
        await this.fetchEmployeeResults();
    }

    public async onEmployeeResultsPageChange(page: number, pageSize?: number): Promise<void> {
        runInAction(() => {
            this.employee_results_current_page = page;
            if (pageSize !== undefined) {
                this.employee_results_page_size = pageSize;
            }
        });
        await this.fetchEmployeeResults();
    }

    public onChangeTab(tab: number): void {
        if (tab === 1) {
            // Fetch employee results when switching to results tab
            this.fetchEmployeeResults();
        } else if (tab === 0) {
            // Questions tab - no additional action needed
        }
        this.activeTab = tab;
    }

    public async init(config: any): Promise<void> {
        runInAction(() => {
            this.folder_id = config?.folder_id || null;
            this.training_id = config?.training_id || null;
        });
        if (!this.isNewTraining) {
            this.fetchTraining();
        }
    }

    public setFolderId(id: number): void {
        runInAction(() => {
            if (this.parent_folders.find(folder => folder.id === id)) {
                this.folder_id = id;
            }
        });
    }

    get currentParentFolder(): any {
        return this.parent_folders.find(folder => folder.id === this.folder_id) || null;
    }

    get allParentFolders(): any[] {
        return this.parent_folders.map(folder => {
            return {
                id: folder.id,
                name: folder.name,
            };
        });
    }

    public async fetchTraining(): Promise<void> {
        runInAction(() => {
            this.training_loading = true;
        });
        const trainingResult: any = await this.sdk.fetchApi('training', 'get', {
            training_id: this.training_id,
        });
        runInAction(() => {
            this.training_loading = false;
        });
        if (trainingResult?.status === 200) {
            runInAction(() => {
                const { training, s3url, folderList } = trainingResult?.json || {};

                // Handle s3url for images and PDFs
                if (typeof s3url === 'object') {
                    this.s3url = s3url?.training || '';
                    this.s3urlPdf = s3url?.pdf || '';
                } else {
                    this.s3url = s3url || '';
                    this.s3urlPdf = '';
                }

                this.training_name = training?.name || '';
                this.training_description = training?.description || '';
                this.folder_id = training?.folder_id || null;
                this.training_content_type = training?.content_type || 'video';
                this.training_video = training?.content_url || '';
                this.training_content_file = training?.content_file || null;
                this.training_image_url = training?.image;
                this.training_active = training?.status === 1;
                this.parent_folders = folderList || [];

                if (training.image) {
                    this.training_image_uuid = training.image;
                }

                let nQuestionArray: TrainingQuestion[] = [];

                (training.questions || []).forEach((question: any) => {
                    let newQuestion: any = {
                        id: question.question_id,
                        text: question.question,
                        pos: question.pos,
                        answers: [],
                    };

                    // Add the answers
                    (question.answers || []).forEach((answer: any) => {
                        newQuestion.answers.push({
                            id: answer.answer_id,
                            text: answer.answer,
                            isCorrect: answer.is_correct,
                        });
                    });

                    nQuestionArray.push(newQuestion);
                });

                this.training_questions = nQuestionArray;
                this._cache = this.cache;
            });
        }
    }

    public async createOrUpdateTraining(): Promise<any> {
        if (this.isValid) {
            runInAction(() => {
                this.training_loading = true;
            });
            // 1. - Upload image if it is a new one
            if (this.training_image) {
                const uploadResult = await this.sdk.uploadImage(
                    'training',
                    'upload',
                    this.training_image
                );
                if (uploadResult?.json?.fileId) {
                    this.training_image_uuid = uploadResult.json.fileId;
                }
            }
            const trainingData = {
                training_id: this.training_id || undefined,
                folder_id: this.folder_id || null,
                name: this.trainingName,
                description: this.trainingDescription || '',
                content_type: this.training_content_type,
                content_url:
                    this.training_content_type === 'video' ? this.trainingVideo || null : null,
                content_file:
                    this.training_content_type === 'pdf'
                        ? this.training_content_file || null
                        : null,
                question_order: this.allQuestionOrder.map((id: number) => id),
                image: this.training_image_uuid,
                status: this.training_active ? 1 : 0,
            };

            const responseTraining = await this.sdk.fetchApi(
                'training',
                this.isNewTraining ? 'add' : 'update',
                trainingData
            );

            runInAction(() => {
                this.training_loading = false;
            });

            if (responseTraining.status === 201 || responseTraining.status === 200) {
                const { training_id } = responseTraining.json || {};

                this.training_id = training_id;
                return {
                    id: training_id,
                    event: this.isNewTraining ? 'NEW' : 'UPDATE',
                };
            }
        }

        return {
            id: null,
            event: null,
        };
    }

    // This method will change the order of the questions
    public changeOrderQuestion(question_a: number, question_b: number): void {
        const questionA = this.training_questions.find((q: any) => q.id === question_a);
        const questionB = this.training_questions.find((q: any) => q.id === question_b);

        if (questionA && questionB) {
            const startPos = Math.min(questionA.pos, questionB.pos);
            const endPos = Math.max(questionA.pos, questionB.pos);

            if (questionA.pos < questionB.pos) {
                // Moving questionA down
                this.training_questions.forEach((question: any) => {
                    if (question.pos >= startPos && question.pos <= endPos) {
                        if (question.id === questionA.id) {
                            question.pos = endPos;
                        } else {
                            question.pos -= 1;
                        }
                    }
                });
            } else {
                // Moving questionA up
                this.training_questions.forEach((question: any) => {
                    if (question.pos >= startPos && question.pos <= endPos) {
                        if (question.id === questionA.id) {
                            question.pos = startPos;
                        } else {
                            question.pos += 1;
                        }
                    }
                });
            }

            // Replace the array to trigger MobX reactions
            this.training_questions = [...this.training_questions];
        }
    }

    set trainingName(name: string) {
        // limit to 80 characters
        if (name.length <= 80) {
            runInAction(() => {
                this.training_name = name;
            });
        }
    }

    get trainingName(): string {
        return this.training_name || '';
    }

    get isTrainingNameValid(): boolean {
        return this.trainingName.length >= 3 && this.trainingName.length <= 80;
    }

    set trainingVideo(url: string) {
        // limit to 80 characters
        if (url.length <= 250) {
            runInAction(() => {
                this.training_video = url;
            });
        }
    }

    get trainingVideo(): string {
        return this.training_video || '';
    }

    get isTrainingVideoValid(): boolean {
        try {
            // Use the URL constructor to validate the URL
            new URL(this.training_video);
            return this.training_video.length <= 250; // Ensure it also meets the length requirement
        } catch {
            return false; // Invalid URL
        }
    }

    set trainingDescription(txt: string) {
        // limit to 80 characters
        if (txt.length <= 250) {
            runInAction(() => {
                this.training_description = txt;
            });
        }
    }

    get trainingDescription(): string {
        return this.training_description || '';
    }

    get trainingContentType(): 'video' | 'pdf' {
        return this.training_content_type;
    }

    set trainingContentType(type: 'video' | 'pdf') {
        runInAction(() => {
            this.training_content_type = type;
            // Reset the other content type fields when switching
            if (type === 'video') {
                this.training_content_file = null;
            } else {
                this.training_video = '';
            }
        });
    }

    get trainingContentFile(): string | null {
        return this.training_content_file;
    }

    set trainingContentFile(fileId: string | null) {
        runInAction(() => {
            this.training_content_file = fileId;
        });
    }

    get isTrainingContentValid(): boolean {
        if (this.training_content_type === 'video') {
            return this.isTrainingVideoValid;
        } else if (this.training_content_type === 'pdf') {
            return !!this.training_content_file;
        }
        return false;
    }

    get trainingPdfUrl(): string | null {
        if (this.training_content_type === 'pdf' && this.training_content_file && this.s3urlPdf) {
            return `${this.s3urlPdf}${this.training_content_file}.pdf`;
        }
        return null;
    }

    get id(): string {
        return 'edittraining';
    }

    get cache(): string {
        return JSON.stringify({
            folder_id: this.folder_id,
            training_name: this.training_name,
            training_description: this.training_description,
            training_content_type: this.training_content_type,
            training_video: this.training_video,
            training_content_file: this.training_content_file,
            training_questions: this.training_questions,
        });
    }

    get isNewTraining(): boolean {
        return this.training_id === null;
    }

    get buttonLabel(): string {
        return this.isNewTraining ? 'Schulung erstellen' : 'Speichern + schließen';
    }

    get header(): string {
        return this.isNewTraining ? 'Neue Schulung' : 'Schulung bearbeiten';
    }

    get isValid(): boolean {
        return this.isTrainingNameValid && this.isTrainingContentValid;
    }

    get hasChanged(): boolean {
        return this._cache !== this.cache || !!this.training_image;
    }

    get allQuestions(): TrainingQuestion[] {
        return this.training_questions.slice().sort((a: any, b: any) => a.pos - b.pos);
    }

    get allQuestionOrder(): Array<number> {
        return this.training_questions
            .slice()
            .sort((a: any, b: any) => a.pos - b.pos)
            .map((question: any) => question.id);
    }

    get image(): any {
        return this.training_image;
    }

    get imageBlob(): string {
        if (!this.training_image_url) {
            return '';
        }
        return `${this.s3url}${this.training_image_url}.jpeg`;
    }

    public setImage(img: any): void {
        this.training_image = img;
    }

    public openVideoUrl(): void {
        if (this.training_content_type === 'video' && this.isTrainingVideoValid) {
            window.open(this.trainingVideo, '_blank');
        }
    }

    public openPdfUrl(): void {
        if (this.training_content_type === 'pdf' && this.trainingPdfUrl) {
            window.open(this.trainingPdfUrl, '_blank');
        }
    }

    public async uploadPdf(file: File): Promise<boolean> {
        try {
            const uploadResult = await this.sdk.uploadPdf('training', 'upload/pdf', file);

            if (uploadResult?.json?.fileId) {
                runInAction(() => {
                    this.training_content_file = uploadResult.json.fileId;
                });
                return true;
            }
        } catch (error) {
            console.error('Error uploading PDF:', error);
        }
        return false;
    }

    public openQuestionEditor(qid: number): void {
        if (this.editor?.id === qid) {
            this.editor = null;
            return;
        }
        const question: any = this.training_questions.find((q: any) => q.id === qid);

        if (question) {
            this.editor = new TrainingQuestionEditor(question);
        } else {
            this.editor = null;
        }
    }

    public closeQuestionEditor(): void {
        this.editor = null;
    }

    public setMenuActiveQuestion(qid: number): void {
        if (this.menu_active_question === qid) {
            this.menu_active_question = null;
            return;
        }
        this.menu_active_question = qid;
    }

    get menuActiveQuestion(): number | null {
        return this.menu_active_question;
    }

    public async onAddQuestion(): Promise<any> {
        runInAction(() => {
            this.editor = new TrainingQuestionEditor(null);
        });
    }

    public async onSaveQuestion(): Promise<boolean> {
        if (this.editor?.isValid) {
            const questionData = {
                question_id: this.editor.id || undefined,
                training_id: this.training_id,
                question: {
                    question: this.editor.question?.text || '',
                    answers: this.editor.answers.map((answer: TrainingQuestionAnswer) => ({
                        answer_id: answer.id || undefined,
                        answer: answer.text,
                        is_correct: answer.isCorrect,
                    })),
                },
            };
            const responseQuestion = await this.sdk.fetchApi(
                'training',
                this.editor.isNewQuestion ? 'question/add' : 'question/update',
                questionData
            );

            if (responseQuestion.status === 201 || responseQuestion.status === 200) {
                return true;
            }
        }
        return false;
    }

    public async deleteQuestion(question_id: number): Promise<MatenRequestResponse> {
        if (question_id) {
            const questionData = {
                question_id,
            };
            const responseQuestion = await this.sdk.fetchApi(
                'training',
                'question/delete',
                questionData
            );

            if (responseQuestion.status === 200) {
                this.fetchTraining();
                return { success: true, status: 200, data: responseQuestion.json };
            }
        }
        return { success: false, status: 400, error: 'Unbekannter Fehler' };
    }

    public toggleActive(): void {
        this.training_active = !this.training_active;
    }

    get isActive(): boolean {
        return this.training_active;
    }
}

export default TrainingEditDialogStore;
